/**
 * Budget and recurring domain hooks (Wave 1A).
 *
 * Same pattern as `use-finance-query.ts`: each hook encapsulates the factory's
 * canonical queryKey + Drizzle query builder; consumers never call
 * `createPowerSyncQuery` directly for this data.
 *
 * The user's financial settings (base_currency, month_start_day) are read
 * with `useFinanceSettings` (use-exchange-rates-query.ts).
 */

import type { Accessor } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { Budget, PowerSyncDrizzleDB, RecurringRule } from "@cashsync/powersync";

import {
  type BudgetPeriodRanges,
  type BudgetSpendRow,
  getBudgetSpendQuery,
  getBudgetsQuery,
  getRecurringRulesQuery,
  queryKeys,
} from "../index";

import { createPowerSyncQuery, DEFAULT_THROTTLE_MS } from "./create-power-sync-query";

interface BaseFinanceQueryOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  staleTime?: number;
  throttleTime?: number;
}

// ─── Budgets ──────────────────────────────────────────────────────────────────

/** User budgets (global and per category). */
export function useBudgetsQuery(options: BaseFinanceQueryOptions) {
  return createPowerSyncQuery<Budget>({
    queryKey: () => queryKeys.budgets.list(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getBudgetsQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

interface UseBudgetProgressQueryOptions extends BaseFinanceQueryOptions {
  /** Current/previous ranges by period type (see `budgetPeriodRanges`). */
  ranges: Accessor<BudgetPeriodRanges>;
}

/**
 * Spend aggregated by budget (current + previous period). Map every row with
 * `computeBudgetProgress` to get progress (including rollover).
 */
export function useBudgetProgressQuery(options: UseBudgetProgressQueryOptions) {
  return createPowerSyncQuery<BudgetSpendRow>({
    queryKey: () =>
      queryKeys.budgets.progress(
        options.userId(),
        options.ranges().month.current.fromISO,
        options.ranges().month.current.toISO,
      ),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getBudgetSpendQuery(db, uid, options.ranges()) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

// ─── Recurring ────────────────────────────────────────────────────────────────

/** All of the user's recurring rules (active and inactive). */
export function useRecurringRulesQuery(options: BaseFinanceQueryOptions) {
  return createPowerSyncQuery<RecurringRule>({
    queryKey: () => queryKeys.recurring.list(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getRecurringRulesQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}
